/**
 * The host operators' access rules as the Users page shows them. `covers` mirrors HostStore.Covers on
 * the server, which is the rule: the page uses it only to avoid offering buttons that would be refused.
 */
import { HostPermission, type HostUserDto } from './contracts.gen.js';

export const PERMISSIONS = [
  {
    flag: HostPermission.ManageOrganizations,
    label: 'Manage organizations',
    hint: 'create, rename, suspend, restart and delete the organizations they can open'
  },
  { flag: HostPermission.ManageUsers, label: 'Manage operators', hint: 'this page, within their own access' },
  { flag: HostPermission.ManageServer, label: 'Manage server builds', hint: 'updates and going back to an earlier build' },
  {
    flag: HostPermission.ManageBilling,
    label: 'Manage billing',
    hint: "organizations' plans and invoices, payments, and the host's mail and PayPal accounts"
  }
] as const;


export function permissionSummary(p: HostPermission): string {
  const names = PERMISSIONS.filter(x => (p & x.flag) === x.flag).map(x => x.label.toLowerCase());
  return names.length === 0 ? 'No host permissions' : names.join(', ').replace(/^./, c => c.toUpperCase());
}

export function organizationSummary(u: HostUserDto): string {
  if (u.allOrganizations) return 'Every organization, as administrator';
  const orgs = u.organizations ?? [];
  if (orgs.length === 0) return 'No organizations';
  return orgs.map(o => `${o.tenantName} (${o.administrator ? 'administrator' : 'technician'})`).join(', ');
}

/** Whether `actor` holds everything `target` has: the server's rule for who may change whom. */
export function covers(actor: HostUserDto, target: HostUserDto): boolean {
  if ((actor.permissions & target.permissions) !== target.permissions) return false;
  if (target.allOrganizations) return actor.allOrganizations;
  if (actor.allOrganizations) return true;
  const mine = actor.organizations ?? [];
  return (target.organizations ?? []).every(o => {
    const m = mine.find(x => x.tenantId === o.tenantId);
    return m !== undefined && (m.administrator || !o.administrator);
  });
}

